/**
 * FSRS — the scheduler (SPEC §7, BRIEF <architecture>: "implement FSRS. Do not invent your own
 * interval algorithm").
 *
 * Pure functions. No I/O, no `Date.now()`, no unseeded randomness: `now` is always supplied by the
 * caller from the injected clock. This is what makes invariants 1 and 2 testable at all.
 *
 * This is FSRS-4.5: the power forgetting curve `R(t) = (1 + FACTOR·t/S)^DECAY` with the published
 * default weights. RESEARCH.md's finding that equal and expanding spacing are statistically
 * equivalent (Kim & Webb 2022) is the reason there is no bespoke expanding-interval layer bolted
 * on top of this — the complexity budget goes to retrieval quality instead.
 *
 * T19a: types, weights, `retrievability()`, `intervalDays()`.
 * T19b: `schedule()` for the `review` state.
 * T19c: `new` and `learning`.
 * T19d: lapses, `relearning`, and the single clamp choke point.
 */

import type { Memory, MemoryState } from '../store/schema';

export type { Memory, MemoryState };

/**
 * Three ratings, not four. `easy` would need a latency threshold nobody has a value for, and FSRS
 * schedules correctly without it (DECISIONS D-020). Derived from grading, never self-reported —
 * there is no "how did that feel?" dialog anywhere in this game.
 */
export type Rating = 'again' | 'hard' | 'good';

export interface SchedulerConfig {
  /** FSRS weights. 17 of them for FSRS-4.5. */
  w: readonly number[];
  /** Target probability of recall at the moment an item comes due. */
  requestRetention: number;
  /** Days. Caps runaway intervals on a very stable item. */
  maximumInterval: number;
}

/**
 * Published FSRS-4.5 default weights. Not tuned, and deliberately not tuned: parameter fitting
 * needs a review history this learner does not have yet, and inventing weights would be inventing
 * an interval algorithm by the back door.
 *
 * These are the LATER of two published FSRS-4.5 default sets, taken from `ts-fsrs` v3.5.7 — the
 * same values the Anki builds of that era shipped. Same algorithm and the same equations as the
 * originally-published set, re-fitted on a much larger dataset. Chosen because this learner runs
 * on untuned defaults for months before there is any review history to fit against, so the priors
 * are doing real work, and better-fitted priors are the whole of the difference. See DECISIONS
 * D-064 and D-065.
 *
 * The originally-published FSRS-4.5 set, for reference:
 *   [0.4872, 1.4003, 3.7145, 13.8206, 5.1618, 1.2298, 0.8975, 0.031, 1.6474,
 *    0.1367, 1.0461, 2.1072, 0.0793, 0.3246, 1.587,  0.2272, 2.8755]
 *
 * VERIFIED 2026-08-22 against upstream. `DECAY` and `FACTOR` below were confirmed in the Open
 * Spaced Repetition org's algorithm reference (`awesome-fsrs` wiki, "The Algorithm") and
 * independently in `open-spaced-repetition/fsrs-rs`, where `FACTOR` is written literally as
 * `19f64 / 81f64`. Seventeen parameters is the version fingerprint: FSRS-5 has nineteen.
 */
export const DEFAULT_WEIGHTS: readonly number[] = [
  0.5701, 1.4436, 4.1386, 10.9355, 5.1443, 1.2006, 0.8627, 0.0362, 1.629, 0.1342, 1.0166, 2.1174,
  0.0839, 0.3204, 1.4676, 0.219, 2.8237,
];

/** Power-curve constants from FSRS-4.5. `FACTOR` is chosen so that R = 0.9 exactly at t = S. */
export const DECAY = -0.5;
export const FACTOR = 19 / 81;

export const DEFAULT_CONFIG: SchedulerConfig = Object.freeze({
  w: DEFAULT_WEIGHTS,
  requestRetention: 0.9,
  /** 100 years. An interval this long means the item is simply known. */
  maximumInterval: 36_500,
});

const MS_PER_DAY = 86_400_000;

/** Difficulty is clamped to [1, 10] in exactly one place (SPEC §7). This is that place. */
export const DIFFICULTY_MIN = 1;
export const DIFFICULTY_MAX = 10;

/** Stability floor, in days. Below this the forgetting curve stops meaning anything. */
export const MIN_STABILITY = 0.1;

export function clampDifficulty(difficulty: number): number {
  return Math.min(DIFFICULTY_MAX, Math.max(DIFFICULTY_MIN, difficulty));
}

/**
 * Whole days elapsed between two instants, never negative.
 *
 * A backwards clock jump — a device correcting its time, a timezone change mid-run — must not
 * hand the scheduler negative elapsed time and inflate stability. It reads as "no time passed",
 * which is the conservative answer: the item stays due.
 */
export function elapsedDays(from: number | null, now: number): number {
  if (from === null) return 0;
  return Math.max(0, (now - from) / MS_PER_DAY);
}

/**
 * Probability of successful recall right now, from the FSRS-4.5 forgetting curve.
 *
 * Returns 0 for an item that has never been reviewed. An unseen item is not "perfectly
 * remembered"; treating it as R = 1 would let it sort behind genuinely due reviews and quietly
 * starve intake. 0 says what is true: there is nothing to recall yet.
 */
export function retrievability(mem: Memory, now: number): number {
  if (mem.lastReviewAt === null || mem.stability <= 0) return 0;
  const t = elapsedDays(mem.lastReviewAt, now);
  return Math.pow(1 + (FACTOR * t) / mem.stability, DECAY);
}

/**
 * The interval, in whole days, at which retrievability will have decayed to `requestRetention`.
 *
 * Inverse of the forgetting curve. Floored at 1 day — a sub-day interval would mean re-showing an
 * item in the same sitting, which is massed practice wearing spacing's clothes (RESEARCH §2).
 */
export function intervalDays(stability: number, cfg: SchedulerConfig = DEFAULT_CONFIG): number {
  if (!(stability > 0)) return 1;
  const raw = (stability / FACTOR) * (Math.pow(cfg.requestRetention, 1 / DECAY) - 1);
  return Math.min(cfg.maximumInterval, Math.max(1, Math.round(raw)));
}

/** `dueAt` for an item reviewed at `now` and given `intervalDays` days. */
export function dueAtFrom(now: number, days: number): number {
  return now + days * MS_PER_DAY;
}

/** An item is due when the clock has reached its `dueAt`. Nothing else gates a review (invariant 7). */
export function isDue(mem: Memory, now: number): boolean {
  return now >= mem.dueAt;
}

// ---------------------------------------------------------------------------------------------
// FSRS-4.5 update equations
// ---------------------------------------------------------------------------------------------

/** FSRS grade numbers. `easy` (4) is never produced by grading; it survives only as the
 *  mean-reversion target inside the difficulty update, where the reference algorithm uses it. */
const GRADE: Record<Rating, number> = { again: 1, hard: 2, good: 3 };
const GRADE_EASY = 4;

function w(cfg: SchedulerConfig, index: number): number {
  const value = cfg.w[index];
  if (value === undefined) throw new RangeError(`SchedulerConfig.w is missing index ${index}`);
  return value;
}

/** Difficulty an item is born with, from the rating of its very first review. */
export function initialDifficulty(rating: Rating, cfg: SchedulerConfig = DEFAULT_CONFIG): number {
  return clampDifficulty(w(cfg, 4) - (GRADE[rating] - 3) * w(cfg, 5));
}

/** Stability an item is born with, from the rating of its very first review. */
export function initialStability(rating: Rating, cfg: SchedulerConfig = DEFAULT_CONFIG): number {
  return Math.max(w(cfg, GRADE[rating] - 1), MIN_STABILITY);
}

/**
 * Difficulty after a review, with FSRS's mean reversion toward the difficulty an `easy` first
 * review would have produced. The reversion is what stops a run of `hard` ratings from pinning an
 * item at 10 forever after one bad week — which matters for a sleep-degraded learner whose bad
 * weeks are not evidence about the item.
 */
export function nextDifficulty(
  difficulty: number,
  rating: Rating,
  cfg: SchedulerConfig = DEFAULT_CONFIG,
): number {
  const moved = difficulty - w(cfg, 6) * (GRADE[rating] - 3);
  const target = clampDifficulty(w(cfg, 4) - (GRADE_EASY - 3) * w(cfg, 5));
  const reverted = w(cfg, 7) * target + (1 - w(cfg, 7)) * moved;
  return clampDifficulty(reverted);
}

/** Stability after a successful recall. Grows more the longer the item was left to decay. */
export function nextRecallStability(
  difficulty: number,
  stability: number,
  r: number,
  rating: Rating,
  cfg: SchedulerConfig = DEFAULT_CONFIG,
): number {
  const hardPenalty = rating === 'hard' ? w(cfg, 15) : 1;
  return (
    stability *
    (1 +
      Math.exp(w(cfg, 8)) *
        (11 - difficulty) *
        Math.pow(stability, -w(cfg, 9)) *
        (Math.exp((1 - r) * w(cfg, 10)) - 1) *
        hardPenalty)
  );
}

/**
 * Stability after a lapse.
 *
 * Clamped to never exceed the stability the item already had. The bare FSRS-4.5 expression can
 * return a larger value than the pre-lapse stability for a very weak, very difficult item, and
 * "you forgot it, so we will wait longer next time" is not a claim this scheduler should ever be
 * able to make. See DECISIONS D-029.
 */
export function nextForgetStability(
  difficulty: number,
  stability: number,
  r: number,
  cfg: SchedulerConfig = DEFAULT_CONFIG,
): number {
  const raw =
    w(cfg, 11) *
    Math.pow(difficulty, -w(cfg, 12)) *
    (Math.pow(stability + 1, w(cfg, 13)) - 1) *
    Math.exp((1 - r) * w(cfg, 14));
  return Math.max(MIN_STABILITY, Math.min(raw, stability));
}

// ---------------------------------------------------------------------------------------------
// schedule()
// ---------------------------------------------------------------------------------------------

/**
 * Apply one qualifying attempt to an item's memory and return the new memory.
 *
 * Pure: `mem` is never mutated, and `now` comes from the caller's injected clock.
 *
 * CALL SITE RULE (invariants 1 and 2): this is called **only** when `advancesInterval(attempt)` is
 * true — production mode, unhinted, first attempt on the item in this run. A non-qualifying
 * attempt is logged and changes nothing here. `scheduler/gate.ts` (T21) owns that check and proves
 * it.
 */
export function schedule(
  mem: Memory,
  rating: Rating,
  now: number,
  cfg: SchedulerConfig = DEFAULT_CONFIG,
): Memory {
  const next =
    mem.state === 'new'
      ? scheduleNew(mem, rating, cfg)
      : scheduleSeen(mem, rating, now, cfg);
  return finalise(next, now, cfg);
}

/** Everything `schedule()` decides before the clamps are applied. */
interface Unclamped {
  stability: number;
  difficulty: number;
  reps: number;
  lapses: number;
  state: MemoryState;
}

/**
 * THE single choke point for both clamps (SPEC §7, TASKS T19d).
 *
 * Difficulty is clamped to [1, 10] and the interval to [1, `maximumInterval`] here and only here,
 * on every path out of `schedule()`. The individual equations also clamp difficulty, but a clamp
 * that lives inside four call sites is four places to forget it; this one cannot be bypassed
 * without deleting it, and a test asserts every state honours both bounds.
 */
function finalise(next: Unclamped, now: number, cfg: SchedulerConfig): Memory {
  // A non-positive or non-finite stability would make the forgetting curve meaningless. Floored,
  // not trusted — `Math.max` alone does not catch NaN, so the finite check comes first.
  const stability = Number.isFinite(next.stability) ? Math.max(MIN_STABILITY, next.stability) : MIN_STABILITY;
  return {
    stability,
    difficulty: clampDifficulty(next.difficulty),
    reps: next.reps,
    lapses: next.lapses,
    lastReviewAt: now,
    dueAt: dueAtFrom(now, intervalDays(stability, cfg)),
    state: next.state,
  };
}

/**
 * The item's very first qualifying attempt. There is no prior stability to update, so stability
 * and difficulty are assigned from the rating alone — nothing stale on the record can leak in.
 *
 * `lapses` stays 0 even on `again`: an item that was never learned has nothing to lapse from, and
 * counting it would corrupt the lapse-rate figure the tracker reports. The failure is still
 * recorded — as an `Attempt` with `correct: false`, which is what retention is computed from.
 */
function scheduleNew(mem: Memory, rating: Rating, cfg: SchedulerConfig): Unclamped {
  return {
    stability: initialStability(rating, cfg),
    difficulty: initialDifficulty(rating, cfg),
    reps: mem.reps + 1,
    lapses: 0,
    // A failed first exposure is not yet learned; it stays in `learning` until it is produced
    // correctly once. It is never blocked or rationed on the way there (invariant 7).
    state: rating === 'again' ? 'learning' : 'review',
  };
}

/**
 * Every state that already has real stability and difficulty: `learning`, `review`, `relearning`.
 *
 * The FSRS equations are identical across all three — the states differ only in what a failure
 * *means*, which is a bookkeeping question, not a scheduling one:
 *
 *  - `review` → a failure is a lapse. The item was known and now is not. Counted, and the item
 *    drops to `relearning`.
 *  - `relearning` → a failure is the SAME lapse continuing, not a new one. Counting it again would
 *    inflate the lapse rate for one bad night into what looks like a chronically weak item.
 *  - `learning` → a failure is not a lapse at all; the item was never learned.
 *
 * Nothing here escalates on repeated failure. Invariant 5's forced self-reference encoding step is
 * the response to a second failure, and it lives in the run loop, not in the scheduler.
 */
function scheduleSeen(
  mem: Memory,
  rating: Rating,
  now: number,
  cfg: SchedulerConfig,
): Unclamped {
  const r = retrievability(mem, now);
  const failed = rating === 'again';
  /**
   * Sanitise before the equations, not after. `nextForgetStability` raises `stability + 1` to a
   * fractional power, which is NaN for any stability below -1 — and NaN propagates silently
   * through every later clamp. A record can carry a bad stability from a partial write or a
   * future migration; it must land on the floor, never on a NaN interval that would take the item
   * out of the queue entirely.
   */
  const prior = Number.isFinite(mem.stability)
    ? Math.max(MIN_STABILITY, mem.stability)
    : MIN_STABILITY;
  /**
   * Difficulty gets the same treatment, and for a sharper reason.
   *
   * `nextForgetStability` raises difficulty to a *negative* power, so a difficulty of 0 — the value
   * `newMemory` hands out, and therefore the value any record carries that reached a seen state
   * without passing through `schedule()` — evaluates to Infinity. The clamp against the prior
   * stability (D-029) then silently turns the entire lapse into a no-op: the learner forgets the
   * item and it comes back at exactly its old spacing, with nothing anywhere reporting that the
   * update did nothing. Found by a run-summary test whose fixture carried difficulty 0.
   *
   * A non-finite difficulty lands on the *hard* end rather than the easy one. Every other guard in
   * this file resolves a corrupt record toward "sooner"; a corrupt record must never be able to buy
   * a longer interval.
   */
  const priorDifficulty = Number.isFinite(mem.difficulty)
    ? clampDifficulty(mem.difficulty)
    : DIFFICULTY_MAX;
  // Both stability expressions read the PRE-update difficulty, as in the reference algorithm.
  // Updating difficulty first would change every interval this app ever produces (D-030).
  const stability = failed
    ? nextForgetStability(priorDifficulty, prior, r, cfg)
    : nextRecallStability(priorDifficulty, prior, r, rating, cfg);

  const isNewLapse = failed && mem.state === 'review';
  const state: MemoryState = failed
    ? mem.state === 'learning'
      ? 'learning'
      : 'relearning'
    : 'review';

  return {
    stability,
    difficulty: nextDifficulty(priorDifficulty, rating, cfg),
    reps: mem.reps + 1,
    lapses: mem.lapses + (isNewLapse ? 1 : 0),
    state,
  };
}
